import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import moment from 'moment-timezone';
import { useSelector, useDispatch } from 'react-redux';
import { NON_CUSTODIAL_CONNECTION_STATUS } from '../../../constants/wallet.constants';
import { setSelectedTradeWallet } from '../../../features/ats/atsSlice';
import { enrichWalletFromLinkedSiblings, filterLinkedWalletsForSide, getPaymentAssetLabel, resolveWalletForPaymentSide } from '../../../services/wallet/linkedWallet.utils';
import CardHeading from "../../../components/common/CardHeading/CardHeading.jsx";
import { ConfirmationModal } from "../../../components/common/commonModal/ConfirmationModal/ConfirmationModal.jsx";
import { TZ } from '../../../constants/constants';
import { inputDecimal, toFix } from '../../../helpers/commonHelper';
import { useTradeWallet } from '../../../hooks/useTradeWallet';
import { LEVERAGE_PRESETS, PERPETUAL_MARGIN_MODE, PLACE_ORDER_EXECUTION, PLACE_ORDER_MARKET_MODE } from './placeOrder.constants';
import { getReferencePrice, getOrderLimitViolation, isPositiveDecimalString, clampSafeTradeDecimalInput, multiplyPlainDecimals } from './placeOrder.utils';
import SelectWalletModal from "./SelectWalletModal/SelectWalletModal.jsx";
import './BuySellTab.scss';
import './SelectWalletModal/SelectWalletModal.scss';
import { PLACE_ORDER_MAX_DECIMALS } from './buySellTab.constants';
import { getPlaceOrderInputDecimals, getSubmitDecimalPlaces } from "./buySellTab.utils.jsx";
import PlaceOrderForm from "./components/PlaceOrderForm.jsx";
import SelectedWalletChip from "./components/SelectedWalletChip.jsx";
import useBuySellFullscreen, { useSectionRef } from './hooks/useBuySellFullscreen';
import useInsufficientBalance from './hooks/useInsufficientBalance';
import usePlaceOrderSubmission from './hooks/usePlaceOrderSubmission';
import useSpotWhitelist from './whitelist/useSpotWhitelist';
import { toastMsg } from '../../../components/common/Toast/Toast';
const upToFourDecimalsRegex = /^\d+(\.\d{0,4})?$/;
const BuySellTab = ({
  sectionRef
}) => {
  const rootRef = useRef(null);
  const formikRef = useRef(null);
  const allowanceInFlightRef = useRef(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [selectedSide, setSelectedSide] = useState(0);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [isApprovingAllowance, setIsApprovingAllowance] = useState(false);
  const [approvingAssetLabel, setApprovingAssetLabel] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [createOrderApiError, setCreateOrderApiError] = useState('');
  const [selectedDate, setSelectedDate] = useState(() => moment.tz(TZ).toDate());
  const [selectedTime, setSelectedTime] = useState(() => moment.tz(TZ).format('HH:mm'));
  const [selectedOrderType, setSelectedOrderType] = useState('GTC');
  const [tradeAmountResetKey, setTradeAmountResetKey] = useState(0);
  const [marketMode] = useState(PLACE_ORDER_MARKET_MODE.SPOT);
  const [executionType, setExecutionType] = useState(PLACE_ORDER_EXECUTION.LIMIT);
  const [marginMode, setMarginMode] = useState(PERPETUAL_MARGIN_MODE.CROSS);
  const [leverage, setLeverage] = useState(LEVERAGE_PRESETS[0]);
  const [tpSlEnabled, setTpSlEnabled] = useState(false);
  const [takeProfitPrice, setTakeProfitPrice] = useState('');
  const [stopLossPrice, setStopLossPrice] = useState('');
  const [gasFeeEstimate, setGasFeeEstimate] = useState({
    amount: '0',
    feeCoin: '',
    chainName: ''
  });
  const setBuySellRootRef = useSectionRef(sectionRef, rootRef);
  const isFullscreen = useBuySellFullscreen(rootRef);
  const dispatch = useDispatch();
  const selectedPair = useSelector(state => state?.ats?.selectedPair);
  const activePairBalance = useSelector(state => state?.ats?.activePairBalance);
  const buyOrderBook = useSelector(state => state.ats.buyOrderBook);
  const sellOrderBook = useSelector(state => state.ats.sellOrderBook);
  const {
    linkedWallets,
    linkedWalletsLoading,
    selectedTradeWallet,
    nonCustodialConnectionStatus,
    selectTradeWallet,
    isWalletReady,
    isNonCustodialBlocked
  } = useTradeWallet({
    selectedPair
  });
  const paymentAssetLabel = useMemo(() => getPaymentAssetLabel(selectedPair, selectedSide), [selectedPair, selectedSide]);
  const orderSideLabel = selectedSide === 0 ? 'Buy' : 'Sell';
  const isOrderProcessing = isSubmitted || isApprovingAllowance;
  const walletsForSide = useMemo(() => filterLinkedWalletsForSide(linkedWallets, selectedPair, selectedSide), [linkedWallets, selectedPair, selectedSide]);
  useEffect(() => {
    if (!selectedTradeWallet || !selectedPair || !linkedWallets?.length) return;
    const resolved = resolveWalletForPaymentSide(linkedWallets, selectedTradeWallet, selectedPair, selectedSide);
    if (!resolved) return;
    const sameWallet = resolved.walletId === selectedTradeWallet.walletId && (resolved.coinName ?? '') === (selectedTradeWallet.coinName ?? '');
    if (!sameWallet) {
      dispatch(setSelectedTradeWallet(enrichWalletFromLinkedSiblings(resolved, linkedWallets)));
    }
  }, [dispatch, linkedWallets, selectedPair, selectedSide, selectedTradeWallet]);
  const isLimitOrder = executionType === PLACE_ORDER_EXECUTION.LIMIT;
  const isSpotMode = marketMode === PLACE_ORDER_MARKET_MODE.SPOT;
  const isPerpetualMode = marketMode === PLACE_ORDER_MARKET_MODE.PERPETUAL;
  const {
    whitelistUi,
    whitelistCoin,
    isSpotTradeWhitelistRoute,
    isPairWhitelistFetching,
    isWalletWhitelistFetching,
    isWhitelistSubscribing,
    selectWalletWhitelistBlockReason,
    isSelectWalletBlockedByWhitelist,
    buyActionLoading,
    walletWhitelistStatus,
    handleSelectWallet,
    handleSubscribeWhitelist
  } = useSpotWhitelist({
    selectedPair,
    marketMode,
    selectedSide,
    selectedTradeWallet,
    selectTradeWallet
  });
  const validationSchema = useMemo(() => {
    const bookPrice = getReferencePrice({
      selectedSide,
      sellOrderBook,
      buyOrderBook,
      limitPrice: null
    });
    const referencePrice =
      bookPrice > 0 ? bookPrice : Number(selectedPair?.last_price) > 0 ? Number(selectedPair.last_price) : 0;
    const quantityRule = Yup.string()
      .required('*This Field is required')
      .matches(upToFourDecimalsRegex, `Up to ${PLACE_ORDER_MAX_DECIMALS} decimal places`)
      .test('pair-order-limits', function pairOrderLimits(value) {
        if (value == null || value === '') return true;
        const msg = getOrderLimitViolation({
          quantity: value,
          price: isLimitOrder ? this.parent?.price : null,
          pair: selectedPair,
          // Limit notional uses the typed price only; market uses book/last price.
          referencePrice: isLimitOrder ? null : referencePrice
        });
        return msg ? this.createError({ message: msg }) : true;
      });
    if (!isLimitOrder) {
      return Yup.object({
        quantity: quantityRule,
        price: Yup.string()
      });
    }
    return Yup.object({
      quantity: quantityRule,
      price: Yup.string()
        .required('*This Field is required')
        .matches(upToFourDecimalsRegex, `Up to ${PLACE_ORDER_MAX_DECIMALS} decimal places`)
    });
  }, [buyOrderBook, isLimitOrder, selectedPair, selectedSide, sellOrderBook]);
  const buildOrderPayloadFromValues = useCallback(values => {
    const submitPrice = isLimitOrder
      ? toFix(values.price, getSubmitDecimalPlaces(selectedPair.price_decimal))
      : toFix(
          getReferencePrice({
            selectedSide,
            sellOrderBook,
            buyOrderBook,
            limitPrice: values.price
          }),
          getSubmitDecimalPlaces(selectedPair.price_decimal)
        );
    return {
      pairName: selectedPair?.pair_key,
      pair_key: `${selectedPair?.otherCurrency}/${selectedPair?.baseCurrency}`,
      // Keep plain decimal strings — Number() turns large prices into 1e+24 in UI/JSON.
      quantity: toFix(values.quantity, getSubmitDecimalPlaces(selectedPair.quantity_decimal)),
      price: submitPrice,
      type: isLimitOrder ? 0 : 1,
      side: selectedSide,
      advanceOrderType: selectedOrderType,
      expire_at: moment.tz(TZ).format(),
      marketMode,
      executionType,
      ...(isPerpetualMode ? {
        marginMode,
        leverage,
        tpSl: tpSlEnabled,
        ...(tpSlEnabled ? {
          takeProfit: takeProfitPrice ? toFix(takeProfitPrice, getSubmitDecimalPlaces(selectedPair.price_decimal)) : null,
          stopLoss: stopLossPrice ? toFix(stopLossPrice, getSubmitDecimalPlaces(selectedPair.price_decimal)) : null
        } : {}),
        reduceOnly: false
      } : {})
    };
  }, [buyOrderBook, executionType, isLimitOrder, isPerpetualMode, leverage, marginMode, marketMode, selectedOrderType, selectedPair, selectedSide, sellOrderBook, stopLossPrice, takeProfitPrice, tpSlEnabled]);
  const getGTDExpiryTime = useCallback(() => {
    const date = moment(selectedDate).format('M/D/YYYY');
    return moment.tz(`${date} ${selectedTime}`, 'M/D/YYYY HH:mm', TZ).format();
  }, [selectedDate, selectedTime]);
  const isGtdExpiryInPast = useMemo(() => {
    if (selectedOrderType !== 'GTD' || !selectedDate || !selectedTime) return false;
    const date = moment(selectedDate).format('M/D/YYYY');
    const expiry = moment.tz(`${date} ${selectedTime}`, 'M/D/YYYY HH:mm', TZ);
    return expiry.isValid() && expiry.isBefore(moment.tz(TZ), 'minute');
  }, [selectedOrderType, selectedDate, selectedTime]);
  const {
    handleAllowanceAndConfirm: runAllowanceAndConfirm,
    handleCloseModal
  } = usePlaceOrderSubmission({
    formikRef,
    selectedTradeWallet,
    linkedWallets,
    selectedPair,
    selectedSide,
    setIsSubmitted,
    setShowModal,
    setTradeAmountResetKey,
    setIsApprovingAllowance,
    setApprovingAssetLabel,
    allowanceInFlightRef,
    setCreateOrderApiError,
    getGTDExpiryTime
  });
  const openConfirmationModal = useCallback(values => {
    const bookPrice = isLimitOrder
      ? 0
      : getReferencePrice({
          selectedSide,
          sellOrderBook,
          buyOrderBook,
          limitPrice: null
        });
    const marketRefPrice =
      bookPrice > 0
        ? bookPrice
        : Number(selectedPair?.last_price) > 0
          ? Number(selectedPair.last_price)
          : null;
    const limitViolation = getOrderLimitViolation({
      quantity: values.quantity,
      price: isLimitOrder ? values.price : null,
      pair: selectedPair,
      referencePrice: isLimitOrder ? null : marketRefPrice
    });
    if (limitViolation) {
      toastMsg.error(limitViolation);
      return;
    }
    setCreateOrderApiError('');
    setShowModal(true);
    setModalData(buildOrderPayloadFromValues(values));
  }, [buildOrderPayloadFromValues, buyOrderBook, isLimitOrder, selectedPair, selectedSide, sellOrderBook]);
  const formik = useFormik({
    initialValues: {
      quantity: '',
      price: ''
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async values => {
      if (!isWalletReady || isNonCustodialBlocked) return;
      await runAllowanceAndConfirm(values, {
        buildOrderPayloadFromValues,
        openConfirmationModal
      });
    }
  });
  useEffect(() => {
    formikRef.current = formik;
  }, [formik]);
  useEffect(() => {
    const f = formikRef.current;
    if (!f?.values?.quantity && !f?.values?.price) return;
    void f.validateForm();
  }, [
    selectedPair?.id,
    selectedPair?.minimum_trade,
    selectedPair?.maximum_order_limit,
    selectedPair?.single_order_limit,
    isLimitOrder,
    selectedSide,
    buyOrderBook,
    sellOrderBook
  ]);
  const {
    insufficientBalance,
    balanceInsufficientTooltip
  } = useInsufficientBalance({
    selectedPair,
    formValues: formik.values,
    selectedSide,
    isPerpetualMode,
    leverage,
    buyOrderBook,
    sellOrderBook,
    activePairBalance,
    selectedTradeWallet,
    gasFeeEstimate
  });
  const updateDateTime = data => {
    if (isOrderProcessing) return;
    if (data.resetToNow) {
      const now = moment.tz(TZ);
      setSelectedDate(now.toDate());
      setSelectedTime(now.format('HH:mm'));
      return;
    }
    if (!data.isTime) {
      setSelectedDate(data.date || moment.tz(TZ).toDate());
      return;
    }
    // react-time-picker can emit null while editing; keep last valid HH:mm
    if (data.time == null || data.time === '') return;
    const parsed = moment(data.time, ['HH:mm', 'H:mm', 'h:mm A', 'hh:mm A'], true);
    setSelectedTime(parsed.isValid() ? parsed.format('HH:mm') : data.time);
  };
  const setBtnActive = type => {
    if (isOrderProcessing) return;
    // Keep date/time across GTC/DAY/GTD switches (e.g. PM must survive DAY → GTD).
    if (type === selectedOrderType) return;
    setSelectedOrderType(type);
  };
  const handleOnChange = e => {
    if (isOrderProcessing) return;
    e.preventDefault();
    const {
      value,
      name
    } = e.target;
    const regex = /^\d+(\.\d*)?$/;
    if (regex.test(value)) {
      const pairDecimal = name === 'price' ? selectedPair.price_decimal : selectedPair.quantity_decimal;
      const decimal = getPlaceOrderInputDecimals(pairDecimal);
      const clamped = clampSafeTradeDecimalInput(value, decimal);
      formik.setFieldValue(name, inputDecimal(clamped, decimal));
    } else if (value.length === 0) {
      formik.setFieldValue(name, '');
    }
  };
  const handleUpDownArrow = (operator, input) => {
    if (isOrderProcessing) return;
    const pairDecimal = input === 'price' ? selectedPair.price_decimal : selectedPair.quantity_decimal;
    const decimal = getPlaceOrderInputDecimals(pairDecimal);
    const value = formik.values[input] ? Number(formik.values[input]) : 0;
    const sumVal = decimal <= 0 ? 1 : 1 / Number(`1e${decimal}`);
    if (operator === '-' && (value === '' || Number(value) === 0)) return;
    const output = operator === '+' ? (Number(value) + Number(sumVal)).toFixed(decimal) : (Number(value) - Number(sumVal)).toFixed(decimal);
    formik.setFieldValue(input, String(output));
  };
  const handleTpSlChange = e => {
    if (isOrderProcessing) return;
    e.preventDefault();
    const {
      value,
      name
    } = e.target;
    const regex = /^\d+(\.\d*)?$/;
    const decimal = getPlaceOrderInputDecimals(selectedPair?.price_decimal);
    const setValue = name === 'takeProfit' ? setTakeProfitPrice : setStopLossPrice;
    if (regex.test(value)) {
      const clamped = clampSafeTradeDecimalInput(value, decimal);
      setValue(inputDecimal(clamped, decimal));
    } else if (value.length === 0) {
      setValue('');
    }
  };
  const handleTpSlUpDownArrow = (operator, field) => {
    if (isOrderProcessing) return;
    const decimal = getPlaceOrderInputDecimals(selectedPair?.price_decimal);
    const current = field === 'takeProfit' ? takeProfitPrice : stopLossPrice;
    const setValue = field === 'takeProfit' ? setTakeProfitPrice : setStopLossPrice;
    const value = current ? Number(current) : 0;
    const sumVal = decimal <= 0 ? 1 : 1 / Number(`1e${decimal}`);
    if (operator === '-' && (current === '' || Number(current) === 0)) return;
    const output = operator === '+' ? (Number(value) + Number(sumVal)).toFixed(decimal) : (Number(value) - Number(sumVal)).toFixed(decimal);
    setValue(String(output));
  };
  const handleExecutionTypeChange = type => {
    if (isOrderProcessing) return;
    setExecutionType(type);
    if (type === PLACE_ORDER_EXECUTION.MARKET && !formik.values.price) {
      const ref = getReferencePrice({
        selectedSide,
        sellOrderBook,
        buyOrderBook,
        limitPrice: ''
      });
      if (ref > 0) {
        formik.setFieldValue('price', String(toFix(ref, getPlaceOrderInputDecimals(selectedPair?.price_decimal))));
      }
    }
  };
  const quantityEstimate = useMemo(() => {
    const qty = String(formik.values.quantity ?? '').trim();
    const refPrice = getReferencePrice({
      selectedSide,
      sellOrderBook,
      buyOrderBook,
      limitPrice: formik.values.price
    });
    if (!isPositiveDecimalString(qty) || !(Number(refPrice) > 0) || !selectedPair) {
      return '0';
    }
    const dec = Math.min(getSubmitDecimalPlaces(selectedPair.price_decimal) + getSubmitDecimalPlaces(selectedPair.quantity_decimal), 12);
    return multiplyPlainDecimals(qty, refPrice, dec);
  }, [buyOrderBook, formik.values.price, formik.values.quantity, selectedPair, selectedSide, sellOrderBook]);
  const otherCurrencyLabel = selectedPair?.otherCurrency?.toUpperCase() || '';
  const baseCurrencyLabel = selectedPair?.baseCurrency?.toUpperCase() || '';
  const baseDisabled = !formik.isValid || isSubmitted || isApprovingAllowance || Number(formik.values.quantity) <= 0 || isLimitOrder && Number(formik.values.price) <= 0 || selectedOrderType === 'GTD' && (!selectedDate || !selectedTime) || isGtdExpiryInPast || isPerpetualMode && tpSlEnabled && !takeProfitPrice && !stopLossPrice;
  const sendOrderDisabled = baseDisabled || insufficientBalance || !isWalletReady || isNonCustodialBlocked;
  const sendOrderDisabledReason = useMemo(() => {
    if (!sendOrderDisabled) return '';
    if (isSubmitted) return 'Order is being submitted. Please wait.';
    if (isApprovingAllowance) return 'Token approval is in progress. Please wait.';
    if (isNonCustodialBlocked) {
      if (nonCustodialConnectionStatus === NON_CUSTODIAL_CONNECTION_STATUS.MISMATCH) {
        return 'Connected MetaMask account does not match the linked wallet.';
      }
      return 'Connect your linked wallet in MetaMask to place an order.';
    }
    if (!isWalletReady) return 'Select a wallet to place an order.';
    if (insufficientBalance) {
      return balanceInsufficientTooltip || 'Insufficient balance for this order.';
    }
    if (Number(formik.values.quantity) <= 0) return 'Enter a quantity greater than zero.';
    if (isLimitOrder && Number(formik.values.price) <= 0) return 'Enter a valid limit price.';
    if (selectedOrderType === 'GTD' && (!selectedDate || !selectedTime)) {
      return 'Select date and time for your GTD order.';
    }
    if (isGtdExpiryInPast) {
      return 'GTD expiry must be after the current time.';
    }
    if (isPerpetualMode && tpSlEnabled && !takeProfitPrice && !stopLossPrice) {
      return 'Enter a take profit or stop loss price.';
    }
    if (formik.errors.price) return formik.errors.price;
    if (formik.errors.quantity) return formik.errors.quantity;
    if (!formik.isValid) return 'Complete the required fields before placing your order.';
    return 'Unable to place order right now.';
  }, [balanceInsufficientTooltip, formik.errors.price, formik.errors.quantity, formik.isValid, formik.values.price, formik.values.quantity, insufficientBalance, isApprovingAllowance, isLimitOrder, isNonCustodialBlocked, isPerpetualMode, isSubmitted, isWalletReady, nonCustodialConnectionStatus, isGtdExpiryInPast, selectedDate, selectedOrderType, selectedTime, sendOrderDisabled, stopLossPrice, takeProfitPrice, tpSlEnabled]);
  const getPrimaryButtonTitle = () => {
    if (isSubmitted) return 'Please Wait...';
    if (isPerpetualMode) {
      return selectedSide === 0 ? `Buy / Long ${leverage}x` : `Sell / Short ${leverage}x`;
    }
    return selectedSide === 0 ? `Buy ${otherCurrencyLabel}` : `Sell ${otherCurrencyLabel}`;
  };
  return <>
      <div className={`buySelltab${isFullscreen ? ' is-fullscreen' : ''}`} ref={setBuySellRootRef}>
        <div className="buySelltab__panel">
          <CardHeading title="Place Order" />
          <SelectedWalletChip wallet={selectedTradeWallet} selectedPair={selectedPair} paymentAssetLabel={paymentAssetLabel} onChangeWallet={() => setShowWalletModal(true)} disabled={isOrderProcessing} />
          <div className="buysellBtn d-flex">
            <Button type="button" className={`btn-style bg-green ${selectedSide === 0 ? 'active' : ''}`} onClick={() => setSelectedSide(0)} disabled={isOrderProcessing}>
              {isPerpetualMode ? 'Buy / Long' : 'Buy'}
            </Button>
            <Button type="button" className={`btn-style bg-red ${selectedSide === 1 ? 'active' : ''}`} onClick={() => setSelectedSide(1)} disabled={isOrderProcessing}>
              {isPerpetualMode ? 'Sell / Short' : 'Sell'}
            </Button>
          </div>
          <PlaceOrderForm formik={formik} selectedSide={selectedSide} isPerpetualMode={isPerpetualMode} isSpotMode={isSpotMode} isLimitOrder={isLimitOrder} marginMode={marginMode} setMarginMode={setMarginMode} leverage={leverage} setLeverage={setLeverage} otherCurrencyLabel={otherCurrencyLabel} baseCurrencyLabel={baseCurrencyLabel} buyOrderBook={buyOrderBook} sellOrderBook={sellOrderBook} handleOnChange={handleOnChange} handleUpDownArrow={handleUpDownArrow} quantityEstimate={quantityEstimate} tpSlEnabled={tpSlEnabled} setTpSlEnabled={setTpSlEnabled} setTakeProfitPrice={setTakeProfitPrice} setStopLossPrice={setStopLossPrice} takeProfitPrice={takeProfitPrice} stopLossPrice={stopLossPrice} handleTpSlChange={handleTpSlChange} handleTpSlUpDownArrow={handleTpSlUpDownArrow} tradeAmountResetKey={tradeAmountResetKey} onGasFeeChange={setGasFeeEstimate} selectedPair={selectedPair} selectedOrderType={selectedOrderType} setSelectedOrderType={setSelectedOrderType} setBtnActive={setBtnActive} selectedDate={selectedDate} selectedTime={selectedTime} updateDateTime={updateDateTime} handleExecutionTypeChange={handleExecutionTypeChange} formLocked={isOrderProcessing} primaryActionProps={{
          isWalletReady,
          linkedWalletsLoading,
          isPairWhitelistFetching,
          isSelectWalletBlockedByWhitelist,
          selectWalletWhitelistBlockReason,
          onOpenWalletModal: () => setShowWalletModal(true),
          isNonCustodialBlocked,
          nonCustodialConnectionStatus,
          selectedTradeWallet,
          onConnectWallet: selectTradeWallet,
          isApprovingAllowance,
          approvingAssetLabel,
          isSpotTradeWhitelistRoute,
          isWalletWhitelistFetching,
          walletWhitelistStatus,
          buyActionLoading,
          isSpotMode,
          isWhitelistSubscribing,
          whitelistCoin,
          onSubscribeWhitelist: handleSubscribeWhitelist,
          whitelistWalletErrorMessage: whitelistUi.wallet.errorMessage,
          selectedSide,
          isPerpetualMode,
          leverage,
          otherCurrencyLabel,
          getPrimaryButtonTitle,
          sendOrderDisabled,
          sendOrderDisabledReason
        }} />
        </div>
      </div>
      <ConfirmationModal show={showModal} handleClose={handleCloseModal} data={modalData} isSubmitted={isSubmitted} apiErrorMessage={createOrderApiError} />
      <SelectWalletModal show={showWalletModal} onClose={() => setShowWalletModal(false)} wallets={walletsForSide} loading={linkedWalletsLoading} onSelectWallet={handleSelectWallet} paymentAssetLabel={paymentAssetLabel} orderSideLabel={orderSideLabel} />
    </>;
};
export default BuySellTab;